import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Suspense, lazy } from "react";

import Link from "ds/components/Link";

import styles from "./styles.module.css";
import MarkdownAssistantPanelCodeBlock from "./CodeBlock";

const SyntaxHighlighter = lazy(() => import("../SyntaxHighlighter"));

const components: Partial<Components> = {
  code({ children, className }) {
    // Inline code - no className means it's not a code block
    if (!className) {
      return <code>{children}</code>;
    }

    const match = /language-(\w+)/.exec(className);
    const language = match?.[1];
    const codeString = String(children).trim();

    // Code block without syntax highlighting
    if (!language) {
      return (
        <MarkdownAssistantPanelCodeBlock code={codeString}>
          <code>{children}</code>
        </MarkdownAssistantPanelCodeBlock>
      );
    }

    // Code block with syntax highlighting
    return (
      <MarkdownAssistantPanelCodeBlock code={codeString}>
        <Suspense fallback={<code>{children}</code>}>
          <SyntaxHighlighter value={codeString} language={language} />
        </Suspense>
      </MarkdownAssistantPanelCodeBlock>
    );
  },
  a({ children, href }) {
    // Internal link
    if (href.startsWith("/")) {
      return <Link href={href}>{children}</Link>;
    }

    // External link
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

type MarkdownAssistantPanelProps = {
  children: string;
};

const MarkdownAssistantPanel = ({ children }: MarkdownAssistantPanelProps) => {
  return (
    <section className={styles.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml>
        {children}
      </ReactMarkdown>
    </section>
  );
};

export default MarkdownAssistantPanel;
